import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Transition from "../utils/Transition.js";

function Modal({
  children,
  id,
  ariaLabel,
  show,
  handleClose,
  contentClassName,
}) {
  const modalContent = useRef(null);

  // Close on Escape only while open (do not register a global listener every render)
  useEffect(() => {
    if (!show) return undefined;

    const keyHandler = ({ keyCode }) => {
      if (keyCode !== 27) return;
      handleClose();
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [show, handleClose]);

  const defaultContentClass =
    "bg-primary-6 overflow-auto max-w-6xl w-full max-h-full relative z-10";

  return (
    <Transition
      className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center px-4 sm:px-6 py-8"
      role="presentation"
      show={show}
      enter="transition ease-out duration-200"
      enterStart="opacity-0"
      enterEnd="opacity-100"
      leave="transition ease-out duration-200"
      leaveStart="opacity-100"
      leaveEnd="opacity-0"
    >
      <div className="relative flex min-h-screen w-full items-center justify-center">
        {/* Dimmed overlay: clicks here close the modal (no document-level listener) */}
        <button
          type="button"
          className="fixed inset-0 z-0 bg-black bg-opacity-60 cursor-default border-0 p-0 focus:outline-none"
          style={{
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
          }}
          onClick={handleClose}
          aria-label="Close dialog"
        />

        <div
          id={id}
          ref={modalContent}
          className={contentClassName ?? defaultContentClass}
          role="dialog"
          aria-modal="true"
          aria-labelledby={ariaLabel}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </Transition>
  );
}

export default Modal;

Modal.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element.isRequired,
  ]),
  id: PropTypes.string,
  ariaLabel: PropTypes.string,
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  contentClassName: PropTypes.string,
};
